import { useQuery } from "@tanstack/react-query";
import { analyticsServicesService } from "@/services/analytics-services-service";
import { useHotel } from "@/hooks/useHotel";
import { formatLocalDateParam } from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import type { ServicesListRow } from "@/types/analytics-services";

/** Max page size the analytics list endpoint allows; large enough to cover a hotel's catalogue for client-side top-N selection. */
const TOP_STATS_PAGE_SIZE = 100;

interface TopServiceStat {
  name: string;
  /** Revenue in major currency units (already divided by amountDivisor). */
  revenue: number;
  bookings: number;
  canceled: number;
}

const pickTop = (
  rows: ServicesListRow[],
  compare: (row: ServicesListRow) => number,
): ServicesListRow | null =>
  rows.reduce<ServicesListRow | null>((best, row) => {
    if (compare(row) <= 0) {
      return best;
    }
    if (!best || compare(row) > compare(best)) {
      return row;
    }
    return best;
  }, null);

/**
 * Focused stats for the Services summary block. Fetches the analytics services
 * list (all categories, current month) and derives the top service by revenue,
 * by bookings, and by cancellations client-side. Each list row now carries a
 * `canceled` count (service requests whose latest task is CANCELED).
 */
export const useServiceSummaryStats = () => {
  const { hotelId } = useHotel();

  const now = new Date();
  const from = formatLocalDateParam(
    new Date(now.getFullYear(), now.getMonth(), 1),
  );
  const to = formatLocalDateParam(now);

  const query = useQuery({
    queryKey: ["hotel", hotelId, "services", "summary-stats", from, to],
    queryFn: () =>
      analyticsServicesService.getList({
        hotelId: hotelId ?? "",
        from,
        to,
        category: "ALL",
        page: 1,
        pageSize: TOP_STATS_PAGE_SIZE,
      }),
    enabled: !!hotelId,
    staleTime: 5 * 60 * 1000,
  });

  const rows = query.data?.data ?? [];
  const divisor = query.data?.amountDivisor || 100;

  const toStat = (row: ServicesListRow): TopServiceStat => ({
    name: row.service.name,
    revenue: row.revenue / divisor,
    bookings: row.bookings,
    canceled: row.canceled,
  });

  const topRevenueRow = pickTop(rows, (row) => row.revenue);
  const topOrderedRow = pickTop(rows, (row) => row.bookings);
  const topCanceledRow = pickTop(rows, (row) => row.canceled);

  return {
    highestRevenue: topRevenueRow ? toStat(topRevenueRow) : null,
    mostOrdered: topOrderedRow ? toStat(topOrderedRow) : null,
    mostCanceled: topCanceledRow ? toStat(topCanceledRow) : null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
